import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
	AlertsPosition, Bucket, BucketAlertOptions, BucketSource, BucketState,
	ComponentUpdateOptions, ModalSidebarOptions, OpenDialogOptions, PathResolver
} from '@omegagrid/bucket';
import { ComponentId, Layout, log, utils } from '@omegagrid/core';
import { Command, OpenCommandsOptions } from '@omegagrid/commands';
import { msg } from '@omegagrid/localize';
import { AppComponent, ComponentState } from '../component';
import { Application } from '../container/application';
import { Container } from '../container/container';
import { parseComponentId } from './helpers';
import { TabItem, TabSplitContainerModel } from '@omegagrid/tabs';
import { CreateComponentOptions, OpenHostTabOptions } from '../types';
import { Header } from './header';
import { debounce } from 'ts-debounce';
import { AppHostCommands } from './appHost.commands';
import { Host } from './host';
import { SettingsChangeEvent } from '../container/settings';

/** user setting: the corner of the content area where the alerts stack (see coreSettingsSpecs) */
export const ALERTS_POSITION_SETTING = 'host.alerts.position';

const DEFAULT_BUCKET_SETTINGS: BucketSource = {
	sidemenu: {
		activeIndex: 0,
		items: []
	},
	//editorLayout: tabModel,
	top: {
		rightItems: []
	},
	options: {
		pathPosition: 'bottom',
		modalSidebarWidth: 400,
		sidemenuWideWidth: 140
	}
}

@customElement('rp-app-host')
export class AppHost extends Host implements Layout {

	@query('og-bucket')
	bucket: Bucket;

	@property({type: Object})
	logger = new log.Logger('AppHost');

	@property({type: String})
	title: string;

	@property({type: String})
	homeComponentId: ComponentId;

	@state()
	bucketSettings: BucketSource;

	private createBucketSettings() {
		return utils.mergeDeep<BucketSource>({
			sidemenu: { hidden: this.container.router.anonymousAccess },
			options: {
				componentMode: this.container.router.anonymousAccess ? 'single' : 'tabbed',
				sidemenuExpandable: true,
				alerts: { position: this.alertsPosition },
			}
		} as Partial<BucketSource>, DEFAULT_BUCKET_SETTINGS)
	}

	get alertsPosition(): AlertsPosition {
		return (this.container?.settings.get(ALERTS_POSITION_SETTING) as AlertsPosition) || 'top-center';
	}

	/** the alerts follow the user setting without a reload (the settings editor dispatches `change` on rp.settings) */
	private _onSettingsChange = (e: SettingsChangeEvent) => {
		if (e.detail.id !== ALERTS_POSITION_SETTING || !this.bucket) return;
		this.bucket.options.alerts.position = this.alertsPosition;
		this.bucket.alerts.updatePositions();
	}

	get components() { return this.bucket?.components }
	componentNames = new Map<ComponentId, string>();
	componentCreateOptions = new Map<ComponentId, CreateComponentOptions>();

	get header() { return (this.bucket?.components.get('bucket-topCenter') as Header) }
	get searchBar() { return this.header?.searchBar }

	readonly commands = new AppHostCommands(this);

	refresh() {
		this.componentNames.clear();
		this.bucket?.requestUpdate();
		this.bucket?.container?.updateComponent(0); // this.requestUpdate();
	}

	exec<TResult = void>(command: string, args?: Record<string, unknown>) : TResult {
		return null;
	}

	async willUpdate(changedProperties: Map<PropertyKey, unknown>) {
		this.bucketSettings ??= this.createBucketSettings();
		if (changedProperties.has('container') && this.container) {
			this.container.applications.get('core');
			(changedProperties.get('container') as Container)?.settings.removeEventListener('change', this._onSettingsChange);
			this.container.settings.addEventListener('change', this._onSettingsChange);
		}
		this.updateSidemenu();
	}

	_debounceResize = debounce((_e) => {
		this.bucket.compact = this.compact;

		// TODO: remove when implemented in bucket
		this.components.components.forEach((component: any) => {
			if (component.compact !== undefined) component.compact = this.compact;
		});

		this.layout();
	}, 100);

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('resize', this._debounceResize);
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._debounceResize);
		this.container?.settings.removeEventListener('change', this._onSettingsChange);
	}

	updateSidemenu() {
		const apps = [...this.container.applications.items.values()].filter(app => app.info.sideMenu);
		this.bucketSettings.sidemenu.items = apps.map(app => ({
			id: app.info.name,
			icon: app.info.icon,
			text: app.info.abbreviation,
			longText: msg(app.info.text),
			color: app.info.color,
			tooltip: app.info.description,
			orientation: 'auto'
		}));
		this.requestUpdate();
	}

	createComponent = async (id: ComponentId) => {
		if (!id) return null;
		const componentId = parseComponentId(id);
		const app = componentId.type == 'bucket' ? 'core' : componentId.type;

		let application = await this.container.app(app);
		if (!application) {
			this.logger.warn(`Application '${componentId.type}' not found`);
			application = await this.container.app('core');
		}

		const options = this.componentCreateOptions.get(id);

		let component: AppComponent<Application> | Promise<AppComponent<Application>> = await application.createComponent(id, options);
		if (component) {
			component.app = application;
			component.componentId = id;
			this.logger.info(`Component '${id}' created`);
			if (component instanceof Header) component.text = this.title;
			if ((component as any).compact !== undefined) (component as any).compact = this.compact;
			if (this.componentNames.has(id)) component.name = this.componentNames.get(id);
		} else if (app !== 'core') {
			component = application.createComponent('core-empty', options);
		}

		if (id == 'bucket-topCenter' && component) {
			(component as any).homeComponentId = this.homeComponentId;
		}

		this.componentCreateOptions.delete(id);
		return component;
	}

	getComponent(id: ComponentId) {
		return this.bucket?.components.get(id);
	}

	private _resolvePath: PathResolver = async (id: ComponentId) => {
		const { type } = parseComponentId(id);
		return await this.container.applications.get(type)?.resolvePath(id) ?? [];
	}

	async updated(props: Map<PropertyKey, undefined>) {
		if (props.has('bucketSettings') && this.bucketSettings) {
			this.bucket.setSourceData(this.bucketSettings);
			await this.bucket.updateComplete;
			this.bucket.sidemenu?.requestUpdate();
		}

		if (props.has('homeComponentId') && this.searchBar) {
			this.header.homeComponentId = this.homeComponentId;
		}

		this.bucket.options.pathResolver = this._resolvePath;
		if (props.has('title') && this.searchBar) this.searchBar.text = this.title;
	}

	dispatchStateEvent() {
		this.bucket.dispatchStateEvent();
	}

	// _onBucketState = (e: BucketEvent) => {
	// 	e.stopPropagation();
	// 	this.dispatchStateEvent();
	// }

	_renderCommand = (div: HTMLDivElement, command: Command) => {
		return html`
			<div class="command">
				${command.name}
			</div>
		`;
	}

	render = () => html`
		<og-bucket
			.createComponent="${this.createComponent}"
			?compact="${this.compact}">
		</og-bucket>
	`;

	openCommands(section: string = null) {
		this.commands?.open(section);

		// TODO: fix focus issue
		setTimeout(() => this.commands.commands.focus(), 50);
	}

	async openSidebarComponent(id: ComponentId) {
		await this.bucket.openSidebarComponent(id);
	}

	updateComponent(id: ComponentId, options: ComponentUpdateOptions) {
		if (!this.bucket.components.has(id)) return;
		if (options.id) {
			(this.bucket.components.components.get(id) as AppComponent).componentId = options.id;
		}
		this.bucket.components.update(id, options);
		this.dispatchStateEvent();
	}

	async open<TCompoment = AppComponent<Application, ComponentState>>(id: ComponentId, options?: OpenHostTabOptions) {
		if (options) this.componentCreateOptions.set(id, options);

		options = Object.assign(options ?? {}, { id });
		// replace only an existing component - components.get() would create
		// a missing one through the factory just to rename it right away
		let component = options.replace && this.bucket.components.has(options.replace)
			? await this.bucket.components.get(options.replace) as AppComponent<Application> : null;
		if (component) {
			this.bucket.components.update(options.replace, options);
			component.componentId = id;
		}
		component = await this.bucket.open(options as TabItem) as AppComponent<Application>;

		if (component instanceof LitElement) {
			await component.updateComplete;
		}

		if (component) {
			component.name = options.title || component.name;
		}

		return component as TCompoment;
	}

	openDialog(options?: OpenDialogOptions) {
		return this.bucket.openDialog(options);
	}


	openMenu(options: OpenCommandsOptions) {
		return this.bucket.openMenu({text: '', mode: 'none', ...options});
	}

	async confirmUnsavedChanges(text?: string): Promise<boolean> {
		if (!this.components?.hasUnsavedComponents) return true;

		const names = Array.from(this.components.unsavedComponents)
			.map(id => (this.components.components.get(id) as AppComponent)?.name || this.componentNames.get(id) || id)
			.join(', ');

		const command = await this.openMenu({
			content: 'yes_no',
			text: `[${names}]: ${text ?? `${msg('Discard changes')}?`}`,
		});

		return command?.id == 'Y';
	}

	openModalSidebar(options: ModalSidebarOptions) {
		return this.bucket.openModalSidebar(options);
	}

	openHome() {
		if (!this.homeComponentId) return;
		this.open(this.homeComponentId, {title: msg('Home')});
	}

	showAlert(options: BucketAlertOptions) {
		return this.bucket.showAlert(options);
	}

	setState(state: BucketState) {
		const layout = new TabSplitContainerModel(state.layout);
		layout.walk((item: TabItem) => { this.componentNames.set(item.id, item.title) });
		this.bucket?.setState(state);
	}

	getState() {
		return this.bucket?.getState();
	}

	layout() {
		this.bucket?.layout();
	}

}
